import { EKycVerificationStatus } from '@exam-center/graphql';
import {
  Box,
  Divider,
  EllipsisText,
  Flex,
  ChairAltOutlinedIcon,
  Label,
  Paper,
  Text,
  EventAvailableOutlinedIcon,
  WatchLaterOutlinedIcon,
  PlaceOutlinedIcon,
} from '@shared/components';
import { dayjs } from '@shared/utils';

import { EKYC_STATUS_COLOR, EKYC_STATUS_TEXT } from '../../constants';
import { IdCardIcon } from '../id-card-icon';

export interface RegistrationExamDetailsProps {
  slotProps?: {
    examCourseName: string;
    seatNumber: string;
    roundNumber: number;
    examStartedAt: Date;
    examEndedAt: Date;
    locationName: string;
    province: string;
    status: EKycVerificationStatus;
  };
}

export default function RegistrationExamDetails(props: Readonly<RegistrationExamDetailsProps>) {
  const { slotProps } = props;
  const statusKey = slotProps?.status as keyof typeof EKYC_STATUS_COLOR;
  const labelColor = EKYC_STATUS_COLOR[statusKey];
  const examStartedAt = dayjs(slotProps?.examStartedAt ?? new Date()); // Example start time
  const examEndedAt = dayjs(slotProps?.examEndedAt ?? examStartedAt.add(3, 'hours')); // Example end time
  const formattedStart = examStartedAt.format('วันddddที่ DD-MM-YYYY, HH:mm'); // Day, Date, Time
  const formattedEnd = examEndedAt.format('HH:mm'); // Time only

  // Combine both formatted times
  const result = `${formattedStart}-${formattedEnd} น.`;

  return (
    <Box sx={{ width: '100%' }}>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          width: '100%',
        })}
      >
        <Paper
          sx={{
            width: '100%',
          }}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              width: '100%',
              padding: theme.spacing(6),
            })}
          >
            <EllipsisText variant='h5'>{slotProps?.examCourseName}</EllipsisText>
            <Flex sx={{ gap: 4, color: 'text.secondary' }}>
              <Flex sx={{ gap: 1.5, color: 'text.primary' }}>
                <ChairAltOutlinedIcon />
                <Text variant='body1Semibold'>{'เลขที่นั่งสอบ: ' + slotProps?.seatNumber}</Text>
              </Flex>
              <Flex sx={{ gap: 1.5 }}>
                <EventAvailableOutlinedIcon />
                <Text>{'รอบสอบที่: ' + slotProps?.roundNumber}</Text>
              </Flex>
              <Flex sx={{ gap: 1.5 }}>
                <WatchLaterOutlinedIcon />
                <Text>{result}</Text>
              </Flex>
              <Flex sx={{ gap: 1.5, height: 'max-content', display: { xs: 'none', lg: 'flex' } }}>
                <PlaceOutlinedIcon />
                <EllipsisText
                  sx={{
                    lineHeight: 1.6,
                  }}
                >
                  {slotProps?.locationName + ', ' + slotProps?.province}
                </EllipsisText>
              </Flex>
            </Flex>
            <Flex sx={{ gap: 1.5, height: 'max-content', display: { xs: 'flex', lg: 'none' } }}>
              <PlaceOutlinedIcon />
              <EllipsisText
                sx={{
                  lineHeight: 1.6,
                }}
              >
                {slotProps?.locationName + ', ' + slotProps?.province}
              </EllipsisText>
            </Flex>
            <Divider />
            <Flex sx={{ gap: 1.5 }}>
              <IdCardIcon />
              <Text>สถานะ e-KYC:</Text>
              <Label variant='outlined' color={labelColor} size='large'>
                {EKYC_STATUS_TEXT[slotProps?.status ?? EKycVerificationStatus.NotStarted]}
              </Label>
            </Flex>
          </Flex>
        </Paper>
      </Flex>
    </Box>
  );
}
